import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Conversation,
  type Callbacks,
  type ClientToolsConfig,
  type Status,
} from "@elevenlabs/client";

type ConnectionType = "websocket" | "webrtc";

const SDK_STATUSES = new Set<Status>([
  "disconnected",
  "connecting",
  "connected",
  "disconnecting",
]);

function readSdkStatus(conversation: unknown): Status | undefined {
  if (typeof conversation !== "object" || conversation === null) return undefined;
  const status = Reflect.get(conversation, "status");
  return typeof status === "string" && SDK_STATUSES.has(status as Status)
    ? (status as Status)
    : undefined;
}

export type ElevenLabsStartOptions = {
  signedUrl?: string;
  agentId?: string;
  conversationToken?: string;
  connectionType?: ConnectionType;
  textOnly: boolean;
  overrides?: {
    conversation?: {
      textOnly?: boolean;
    };
  };
  clientTools?: ClientToolsConfig["clientTools"];
  dynamicVariables?: Record<string, string | number | boolean>;
};

export type ElevenLabsEndResult = {
  status: "disconnected";
  teardownError?: Error;
};

export type ElevenLabsConversationAdapter = {
  status: Status;
  isSpeaking: boolean;
  startSession: (options: ElevenLabsStartOptions) => Promise<string>;
  endSession: () => Promise<ElevenLabsEndResult>;
  sendUserMessage: (text: string) => void;
  sendUserActivity: () => void;
  sendContextualUpdate: (text: string) => void;
};

export function useElevenLabsConversation(
  callbacks: Partial<Callbacks>
): ElevenLabsConversationAdapter {
  const callbacksRef = useRef(callbacks);
  const conversationRef = useRef<Awaited<ReturnType<typeof Conversation.startSession>> | null>(null);
  const startPromiseRef = useRef<Promise<string> | null>(null);
  const disposedRef = useRef(false);
  const [status, setStatus] = useState<Status>("disconnected");
  const [isSpeaking, setIsSpeaking] = useState(false);

  callbacksRef.current = callbacks;

  const startSession = useCallback(async (options: ElevenLabsStartOptions): Promise<string> => {
    const activeConversation = conversationRef.current;
    if (activeConversation?.isOpen()) return activeConversation.getId();
    if (activeConversation) {
      throw new Error("Previous conversation shutdown is incomplete");
    }
    if (startPromiseRef.current) return startPromiseRef.current;

    const sharedOptions = {
      textOnly: options.textOnly,
      overrides: options.overrides,
      clientTools: options.clientTools,
      dynamicVariables: options.dynamicVariables,
      ...callbacksRef.current,
      onConnect: (props: Parameters<NonNullable<Callbacks["onConnect"]>>[0]) => {
        if (disposedRef.current) return;
        callbacksRef.current.onConnect?.(props);
      },
      onDisconnect: (details: Parameters<NonNullable<Callbacks["onDisconnect"]>>[0]) => {
        conversationRef.current = null;
        if (disposedRef.current) return;
        setStatus("disconnected");
        setIsSpeaking(false);
        callbacksRef.current.onDisconnect?.(details);
      },
      onStatusChange: (props: Parameters<NonNullable<Callbacks["onStatusChange"]>>[0]) => {
        if (disposedRef.current) return;
        setStatus(props.status);
        callbacksRef.current.onStatusChange?.(props);
      },
      onModeChange: (props: Parameters<NonNullable<Callbacks["onModeChange"]>>[0]) => {
        if (disposedRef.current) return;
        setIsSpeaking(props.mode === "speaking");
        callbacksRef.current.onModeChange?.(props);
      },
    };

    const sdkOptions = options.signedUrl
      ? {
          ...sharedOptions,
          signedUrl: options.signedUrl,
          connectionType: "websocket" as const,
        }
      : options.conversationToken
        ? {
            ...sharedOptions,
            conversationToken: options.conversationToken,
            connectionType: "webrtc" as const,
          }
        : options.agentId
          ? {
              ...sharedOptions,
              agentId: options.agentId,
              connectionType: options.connectionType ?? "webrtc",
            }
          : null;

    if (!sdkOptions) throw new Error("Missing conversation credentials");

    setStatus("connecting");
    const startPromise = Conversation.startSession(sdkOptions)
      .then(async (conversation) => {
        if (disposedRef.current) {
          await conversation.endSession();
          return conversation.getId();
        }
        conversationRef.current = conversation;
        return conversation.getId();
      })
      .catch((error: unknown) => {
        if (!disposedRef.current) setStatus("disconnected");
        throw error;
      });
    startPromiseRef.current = startPromise;

    try {
      return await startPromise;
    } finally {
      startPromiseRef.current = null;
    }
  }, []);

  const endSession = useCallback(async (): Promise<ElevenLabsEndResult> => {
    const pendingStart = startPromiseRef.current;
    if (pendingStart) {
      try {
        await pendingStart;
      } catch {
        return { status: "disconnected" };
      }
    }

    const conversation = conversationRef.current;
    if (!conversation) return { status: "disconnected" };
    try {
      await conversation.endSession();
    } catch (error) {
      if (readSdkStatus(conversation) === "disconnected") {
        if (conversationRef.current === conversation) {
          conversationRef.current = null;
        }
        return {
          status: "disconnected",
          teardownError: error instanceof Error ? error : new Error(String(error)),
        };
      }
      throw error;
    }
    const sdkStatus = readSdkStatus(conversation);
    if (sdkStatus && sdkStatus !== "disconnected") {
      throw new Error(`Conversation shutdown stopped in ${sdkStatus} state`);
    }
    if (conversationRef.current === conversation) {
      conversationRef.current = null;
    }
    return { status: "disconnected" };
  }, []);

  const sendUserMessage = useCallback((text: string): void => {
    conversationRef.current?.sendUserMessage(text);
  }, []);

  const sendUserActivity = useCallback((): void => {
    conversationRef.current?.sendUserActivity();
  }, []);

  const sendContextualUpdate = useCallback((text: string): void => {
    conversationRef.current?.sendContextualUpdate(text);
  }, []);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      const conversation = conversationRef.current;
      if (!conversation) return;
      void conversation.endSession().then(
        () => {
          if (conversationRef.current === conversation) {
            conversationRef.current = null;
          }
        },
        () => undefined
      );
    };
  }, []);

  return useMemo(
    () => ({
      status,
      isSpeaking,
      startSession,
      endSession,
      sendUserMessage,
      sendUserActivity,
      sendContextualUpdate,
    }),
    [
      status,
      isSpeaking,
      startSession,
      endSession,
      sendUserMessage,
      sendUserActivity,
      sendContextualUpdate,
    ]
  );
}
